import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  ButtonGroup,
  Form,
  InputGroup,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Pagination,
  Badge,
  ListGroup,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useOrdersWebSocket } from '../../hooks/useOrdersWebSocket';

const MOBILE_TABLE_BREAKPOINT = 992;

function formatItemDimsWeightLine(item, t) {
  if (!item) return t('returns.dimensionsWeightUnavailable');
  const L = item.sku_length;
  const W = item.sku_width;
  const H = item.sku_height;
  const hasDims =
    (L != null && Number(L) > 0) || (W != null && Number(W) > 0) || (H != null && Number(H) > 0);
  const wkgRaw = item.sku_weight_kg;
  const wkg = wkgRaw != null && wkgRaw !== '' ? Number(wkgRaw) : NaN;
  const hasWeight = !Number.isNaN(wkg) && wkg > 0;
  if (!hasDims && !hasWeight) return t('returns.dimensionsWeightUnavailable');
  const dStr = hasDims
    ? `${L || 0}×${W || 0}×${H || 0} ${t('returns.unitCm')}`
    : t('returns.dimensionsUnknownShort');
  const wStr = hasWeight ? `${wkg} ${t('returns.unitKg')}` : t('returns.weightUnknownShort');
  return `${dStr} · ${wStr}`;
}

function WbProductThumb({ url, name, onOpen, openTitle, size = 48 }) {
  if (!url) return <span className="text-muted small">—</span>;
  const img = (
    <img
      src={url}
      alt={name || ''}
      style={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: 4,
        display: 'block',
        cursor: onOpen ? 'pointer' : 'default',
      }}
    />
  );
  if (!onOpen) return img;
  return (
    <button
      type="button"
      className="p-0 border-0 bg-transparent lh-1"
      title={openTitle}
      aria-label={openTitle}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
    >
      {img}
    </button>
  );
}

function WbCatalogLink({ href, label, className = 'small mt-1 d-inline-block' }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
      <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
    </a>
  );
}

function SuperuserWhPortalProductLink({ orderRow, user, t, className = 'small mt-1 d-inline-block' }) {
  if (!user?.is_superuser) return null;
  const href = (orderRow?.wh_portal_products_url || '').trim();
  if (!href) return null;
  return <WbCatalogLink href={href} label={t('returns.whPortalProducts')} className={className} />;
}

function hasWbPrimaryImageUrl(item) {
  return Boolean(item && String(item.wb_primary_image_url || '').trim());
}

/** Ссылка на карточку WB: из API, иначе по nm_id, иначе sku_link. */
function wbProductCatalogUrl(item) {
  if (!item) return null;
  const u = (item.wb_product_url || '').trim();
  if (u) return u;
  const nm = item.wb_nm_id;
  if (nm != null && nm !== '') {
    const n = Number(nm);
    if (!Number.isNaN(n) && n > 0) {
      return `https://www.wildberries.ru/catalog/${n}/detail.aspx`;
    }
  }
  const link = (item.sku_link || '').trim();
  return link || null;
}

/** Сумма списаний по типу по списку заказов (возвратов). */
function sumWhChargesByType(orders, type) {
  let s = 0;
  (orders || []).forEach((ord) => {
    (ord.wh_billing_expenses || []).forEach((e) => {
      if (e.type === type) s += Math.abs(Number(e.amount || 0));
    });
  });
  return s;
}

const WH_STORAGE_EXPENSE_TYPES = ['storage_warehouse', 'storage_daily'];

/** Сумма всех списаний за хранение (WH + посуточное на Flydarin). */
function sumWhStorageCharges(orders) {
  let s = 0;
  (orders || []).forEach((ord) => {
    (ord.wh_billing_expenses || []).forEach((e) => {
      if (WH_STORAGE_EXPENSE_TYPES.includes(e.type)) s += Math.abs(Number(e.amount || 0));
    });
  });
  return s;
}

/** Суммы по типам хранения для одного заказа. */
function whStorageAmountsByType(order) {
  const out = {};
  (order.wh_billing_expenses || []).forEach((e) => {
    if (WH_STORAGE_EXPENSE_TYPES.includes(e.type)) {
      out[e.type] = (out[e.type] || 0) + Math.abs(Number(e.amount || 0));
    }
  });
  return out;
}

/** CSS-модификатор для цветной подложки статуса маркетплейса. */
function goodsOnSaleMpStatusPillClassName(statusCode) {
  const c = (statusCode || '').trim();
  if (!c) return 'goods-on-sale-mp-status goods-on-sale-mp-status--neutral';
  if (c === 'sold') return 'goods-on-sale-mp-status goods-on-sale-mp-status--sold';
  if (c === 'ready_for_pickup') return 'goods-on-sale-mp-status goods-on-sale-mp-status--pvz';
  if (c === 'canceled' || c === 'canceled_by_client' || c === 'defect') return 'goods-on-sale-mp-status goods-on-sale-mp-status--danger';
  if (c === 'waiting') return 'goods-on-sale-mp-status goods-on-sale-mp-status--waiting';
  if (c === 'sorted') return 'goods-on-sale-mp-status goods-on-sale-mp-status--sorted';
  return 'goods-on-sale-mp-status goods-on-sale-mp-status--neutral';
}

/** Нормализуем последний MP-статус: берём marketplace_status_history.max(date), иначе верхнеуровневые поля. */
function latestMpStatusSnapshot(mp) {
  if (!mp || typeof mp !== 'object') return { status: '', statusDisplay: '', substatus: '' };
  const history = Array.isArray(mp.marketplace_status_history) ? mp.marketplace_status_history : [];
  let latest = null;
  let latestTs = -Infinity;
  history.forEach((row) => {
    const ts = Date.parse(row?.date || '');
    const safeTs = Number.isNaN(ts) ? -Infinity : ts;
    if (safeTs >= latestTs) {
      latest = row;
      latestTs = safeTs;
    }
  });
  return {
    status: String(latest?.status || mp.status || '').trim(),
    statusDisplay: String(latest?.status_display || mp.status_display || '').trim(),
    substatus: String(latest?.substatus || mp.substatus || '').trim(),
  };
}

/** Подпись последнего статуса маркетплейса из WH snapshot. */
function goodsOnSaleMpStatusLabel(mp, t, language) {
  if (!mp) return '—';
  const snap = latestMpStatusSnapshot(mp);
  const code = snap.status;
  const display = snap.statusDisplay;
  const substatus = snap.substatus;
  const enByCode = {
    waiting: 'Preparing for shipment',
    ready_for_pickup: 'Arrived at pickup point (PVZ)',
    sold: 'Sold',
    canceled_by_client: 'Buyer canceled at pickup',
    sorted: 'Order sorted at warehouse',
    defect: 'Assembly canceled — defect',
    canceled: 'Assembly canceled',
  };
  const enByRuDisplay = {
    'Отмена сборочного задания': 'Assembly canceled',
    Отменён: 'Canceled',
    'Сборочное задание в работе': 'Preparing for shipment',
    'Сборочное задание отсортировано': 'Order sorted at warehouse',
    'Отмена сборочного задания по причине брака': 'Assembly canceled — defect',
    'Отмена клиентом на ПВЗ': 'Buyer canceled at pickup',
    'Готов к выдаче': 'Arrived at pickup point (PVZ)',
    Продан: 'Sold',
  };
  const ruToCode = {
    'Отмена сборочного задания': 'canceled',
    'Отменён': 'canceled',
    'Сборочное задание в работе': 'waiting',
    'Сборочное задание отсортировано': 'sorted',
    'Отмена сборочного задания по причине брака': 'defect',
    'Отмена клиентом на ПВЗ': 'canceled_by_client',
    'Готов к выдаче': 'ready_for_pickup',
    'Продан': 'sold',
  };
  const ruDisplayToTranslationKey = {
    'Отмена сборочного задания': 'cancel',
    Отменён: 'cancel',
  };
  const keys = {
    waiting: 'returns.goodsOnSaleMpStateWaiting',
    ready_for_pickup: 'returns.goodsOnSaleMpStateReadyPickup',
    sold: 'returns.goodsOnSaleMpStateSold',
    canceled_by_client: 'returns.goodsOnSaleMpStateCanceledByClient',
    sorted: 'returns.goodsOnSaleMpStateSorted',
    defect: 'returns.goodsOnSaleMpStateDefect',
  };
  const effectiveCode = keys[code] ? code : (ruToCode[display] || '');
  if (language !== 'ru') {
    if (effectiveCode && enByCode[effectiveCode]) return enByCode[effectiveCode];
    if (display && enByRuDisplay[display]) return enByRuDisplay[display];
    if (effectiveCode === 'canceled' || substatus === 'cancel') return 'Canceled';
  }
  if (!effectiveCode) return display || code || '—';
  const tr = keys[effectiveCode];
  if (tr) {
    const translated = t(tr);
    if (translated !== tr) return translated;
  }
  const displayKey = ruDisplayToTranslationKey[display];
  if (displayKey) {
    const translatedDisplay = t(displayKey);
    if (translatedDisplay !== displayKey) return translatedDisplay;
  }
  if (effectiveCode === 'canceled' || substatus === 'cancel') {
    const translatedCancel = t('cancel');
    if (translatedCancel !== 'cancel') return translatedCancel;
  }
  return display || effectiveCode;
}

/** Сопоставление группы возвратов с live-остатками WH (GET /products) по sku/barcode. */
function collectWhStockMatchKeysForOrders(orders) {
  const keys = new Set();
  (orders || []).forEach((ord) => {
    const bc = (ord.sku_barcode || '').trim();
    const sku = (ord.sku_code || '').trim();
    if (bc) keys.add(bc);
    if (sku) keys.add(sku);
  });
  return keys;
}

function resolveWhStocksForOrders(orders, whStockPayload) {
  const products = whStockPayload?.products;
  if (!Array.isArray(products) || !products.length) return null;
  const matchKeys = collectWhStockMatchKeysForOrders(orders);
  if (!matchKeys.size) return null;

  const matchedStocks = [];
  const seen = new Set();
  products.forEach((product) => {
    const pkeys = new Set();
    const sku = (product?.sku || '').trim();
    if (sku) pkeys.add(sku);
    (product?.barcodes || []).forEach((bc) => {
      const v = typeof bc === 'object' ? (bc?.value || '').trim() : String(bc || '').trim();
      if (v) pkeys.add(v);
    });
    const hit = [...matchKeys].some((k) => pkeys.has(k));
    if (!hit) return;
    const dedupe = `${product?.wh_client_id || ''}:${product?.id ?? product?.sku ?? ''}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    if (product?.stocks && typeof product.stocks === 'object') {
      matchedStocks.push(product.stocks);
    }
  });
  if (!matchedStocks.length) return null;

  const out = {};
  matchedStocks.forEach((stocks) => {
    Object.entries(stocks).forEach(([field, val]) => {
      if (val == null || val === '') return;
      const n = Number(val);
      if (Number.isNaN(n)) return;
      out[field] = (out[field] || 0) + n;
    });
  });
  return Object.keys(out).length ? out : null;
}

function formatWhLiveStockLine(stocks) {
  if (!stocks || stocks.present == null || stocks.present === '') return '—';
  const present = Number(stocks.present);
  if (Number.isNaN(present)) return '—';
  return String(present);
}

function whLiveStockTitle(stocks, t) {
  if (!stocks) return '';
  const parts = [];
  if (stocks.present != null && stocks.present !== '') {
    parts.push(`${t('returns.goodsOnSaleWhPresentCol')}: ${stocks.present}`);
  }
  if (stocks.reserved != null && stocks.reserved !== '') {
    parts.push(`${t('returns.goodsOnSaleWhReservedCol')}: ${stocks.reserved}`);
  }
  if (stocks.defected != null && stocks.defected !== '') {
    parts.push(`${t('returns.goodsOnSaleWhDefectedCol')}: ${stocks.defected}`);
  }
  return parts.join(' · ');
}

/** Последнее известное количество на складе WH из списаний storage (по самой свежей billing_date). */
function latestWhStockPresent(order) {
  const storage = (order.wh_billing_expenses || []).filter((e) => e.type === 'storage_warehouse');
  if (!storage.length) return null;
  let best = storage[0];
  let bestTs = best?.billing_date ? Date.parse(best.billing_date) : NaN;
  if (Number.isNaN(bestTs)) bestTs = -Infinity;
  for (let i = 1; i < storage.length; i += 1) {
    const e = storage[i];
    const ts = e?.billing_date ? Date.parse(e.billing_date) : NaN;
    const t = Number.isNaN(ts) ? -Infinity : ts;
    if (t >= bestTs) {
      best = e;
      bestTs = t;
    }
  }
  if (best?.wh_stock_present == null || best.wh_stock_present === '') return null;
  const n = Number(best.wh_stock_present);
  return Number.isNaN(n) ? null : n;
}

/**
 * «Реализовано» (зелёный): ушёл к продавцу / по WH остаток = 0 / есть отгрузка в продажу без положительного остатка.
 * «На складе»: явный wh_stock_present > 0, иначе ещё не отгружено в продажу по биллингу.
 */
function whSaleReturnLane(order) {
  const present = latestWhStockPresent(order);
  if (present != null && present > 0) {
    return 'warehouse';
  }
  if (order.status === 'sent_to_seller') {
    return 'settled';
  }
  if (present === 0) {
    return 'settled';
  }
  const charges = order.wh_billing_expenses || [];
  const hasPackaging = charges.some((e) => e.type === 'packaging_shipment');
  if (hasPackaging || order.status === 'awaiting_shipment') {
    return 'settled';
  }
  return 'warehouse';
}

/** Превью WB, «Not Found», или плейсхолдер «скоро». */
function WbProductThumbOrComingSoon({
  showImage,
  item,
  size,
  wbThumbProps,
  comingSoonText,
  showNotFound,
  notFoundText,
}) {
  if (showNotFound) {
    return (
      <div
        className="d-inline-flex align-items-center justify-content-center border rounded text-muted bg-secondary bg-opacity-10 user-select-none px-1"
        style={{
          width: size,
          height: size,
          fontSize: Math.max(9, Math.min(13, size * 0.24)),
          lineHeight: 1.1,
          textAlign: 'center',
        }}
        title={notFoundText}
      >
        {notFoundText}
      </div>
    );
  }
  if (showImage) {
    return <WbProductThumb {...wbThumbProps(item, size)} />;
  }
  return (
    <div
      className="d-inline-flex align-items-center justify-content-center border rounded text-muted bg-secondary bg-opacity-10 user-select-none px-1"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.min(13, size * 0.24)),
        lineHeight: 1.1,
        textAlign: 'center',
      }}
      title={comingSoonText}
    >
      {comingSoonText}
    </div>
  );
}

const ReturnsTable = ({ activeTab = 'inTransit', onTabChange, selectedStoreId, hideTabNavigation = false }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [returns, setReturns] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  /** Вкладка «Товары в продаже»: развёрнутая группа по товару (nm/sku). */
  const [whSaleExpandedProductKey, setWhSaleExpandedProductKey] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_TABLE_BREAKPOINT);
  /** Сбрасывает устаревший ответ fetch при смене вкладки/фильтров/страницы (иначе пагинация «ломается»). */
  const fetchReturnsSeqRef = useRef(0);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const pageSizeOptions = [10, 25, 50, 100];
  
  // Filter states (debounced search reduces refetches and UI jank while typing)
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [defectFilter, setDefectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [otherPvzFilter, setOtherPvzFilter] = useState('');
  /** Фильтр по дате отгрузки покупателю (OrderWhShipment), только вкладка Goods on sale — черновик в полях, в API уходит по кнопке «Применить». */
  const [whSaleShippedFromDraft, setWhSaleShippedFromDraft] = useState('');
  const [whSaleShippedToDraft, setWhSaleShippedToDraft] = useState('');
  const [appliedWhSaleShippedFrom, setAppliedWhSaleShippedFrom] = useState('');
  const [appliedWhSaleShippedTo, setAppliedWhSaleShippedTo] = useState('');
  /** Сводка с API /returns/wh-billing/ (по всем заказам текущих фильтров) */
  const [whSaleSummary, setWhSaleSummary] = useState(null);
  /** Live-остатки WH GET /products для вкладки «Товары в продаже». */
  const [whSaleWhStock, setWhSaleWhStock] = useState(null);

  // Update state for individual fields
  const [updating, setUpdating] = useState({});

  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkDecisionIds, setBulkDecisionIds] = useState(null);
  
  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalItem, setStatusModalItem] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Delivery address check state
  const [hasDeliveryAddress, setHasDeliveryAddress] = useState(true);
  const [showAddressWarning, setShowAddressWarning] = useState(false);
  
  // Photo lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  /** order_item_id → true: WB Content API не вернул фото. */
  const [wbImageNotFoundByItemId, setWbImageNotFoundByItemId] = useState({});

  const wbMissingThumbKey = useMemo(() => {
    return returns
      .filter(
        (r) =>
          r.item_id &&
          !hasWbPrimaryImageUrl(r) &&
          !wbImageNotFoundByItemId[r.item_id],
      )
      .map((r) => r.item_id)
      .sort((a, b) => a - b)
      .join(',');
  }, [returns, wbImageNotFoundByItemId]);

  const mergeWbMediaChunk = useCallback((rows) => {
    const byItemId = new Map(rows.map((x) => [x.order_item_id, x]));
    setReturns((prev) =>
      prev.map((row) => {
        const u = byItemId.get(row.item_id);
        if (!u) return row;
        if (u.found && u.wb_primary_image_url) {
          return {
            ...row,
            wb_primary_image_url: u.wb_primary_image_url,
            wb_primary_image_big_url: u.wb_primary_image_big_url || u.wb_primary_image_url,
          };
        }
        return row;
      }),
    );
    setWbImageNotFoundByItemId((prev) => {
      const next = { ...prev };
      for (const x of rows) {
        if (!x.order_item_id) continue;
        if (x.found && x.wb_primary_image_url) {
          delete next[x.order_item_id];
        } else if (!x.found) {
          next[x.order_item_id] = true;
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (loading || !wbMissingThumbKey) return undefined;
    const ids = wbMissingThumbKey
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
    if (!ids.length) return undefined;
    let cancelled = false;
    const delay = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      try {
        for (let i = 0; i < ids.length; i += 10) {
          if (cancelled) return;
          const chunk = ids.slice(i, i + 10);
          try {
            const res = await api.postReturnsOrderItemsWbMedia(chunk);
            if (!cancelled) mergeWbMediaChunk(res.items || []);
          } catch (e) {
            console.error(e);
          }
          if (i + 10 < ids.length && !cancelled) await delay(500);
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, wbMissingThumbKey, mergeWbMediaChunk]);

  const openWbProductLightbox = useCallback((imageUrl) => {
    if (!imageUrl) return;
    const payload = { file_url: imageUrl, file_name: 'Wildberries' };
    setLightboxPhotos([payload]);
    setLightboxIndex(0);
    setLightboxPhoto(payload);
  }, []);

  const wbThumbProps = useCallback(
    (item, size = 48) => ({
      url: item.wb_primary_image_url,
      name: item.sku_name,
      size,
      onOpen: item.wb_primary_image_url
        ? () =>
            openWbProductLightbox(item.wb_primary_image_big_url || item.wb_primary_image_url)
        : undefined,
      openTitle: t('returns.wbPhotoFullscreen'),
    }),
    [openWbProductLightbox, t],
  );

  const renderWbThumb = useCallback(
    (item, size = 48) => (
      <WbProductThumbOrComingSoon
        showImage={hasWbPrimaryImageUrl(item)}
        item={item}
        size={size}
        wbThumbProps={wbThumbProps}
        comingSoonText={t('returns.productImageComingSoon')}
        showNotFound={Boolean(item.item_id && wbImageNotFoundByItemId[item.item_id])}
        notFoundText={t('returns.wbImageNotFound')}
      />
    ),
    [wbThumbProps, t, wbImageNotFoundByItemId],
  );

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_TABLE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setWhSaleExpandedProductKey(null);
  }, [activeTab]);

  // Check delivery address on mount
  useEffect(() => {
    const checkAddress = async () => {
      try {
        const response = await api.checkDeliveryAddress();
        setHasDeliveryAddress(response.data?.has_delivery_address || false);
      } catch (err) {
        console.error('Failed to check delivery address:', err);
      }
    };
    checkAddress();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Available statuses state for filter dropdown
  const [availableStatuses, setAvailableStatuses] = useState([]);

  // Status translations map
  const getWhBillingTypeLabel = (type) => {
    if (type === 'storage_warehouse') return t('returns.whServiceStorage');
    if (type === 'storage_daily') return t('billing.type_storage_daily');
    if (type === 'packaging_shipment') return t('returns.whServiceShipmentToSale');
    const key = `billing.type_${type}`;
    const label = t(key);
    return label !== key ? label : type;
  };

  const getStatusLabel = (statusValue) => {
    const statusMap = {
      awaiting_receipt: t('returns.statusAwaitingReceipt'),
      received_awaiting_check: t('returns.statusReceivedAwaitingCheck'),
      awaiting_seller_decision: t('returns.statusAwaitingSellerDecision'),
      awaiting_for_sale: t('returns.statusAwaitingForSale'),
      transferring_to_sale: t('returns.statusTransferringToSale'),
      submitted_for_sale: t('returns.statusSubmittedForSale'),
      awaiting_shipment: t('returns.statusAwaitingShipment'),
      awaiting_disposal: t('returns.statusAwaitingDisposal'),
      disposed: t('returns.statusDisposed'),
      sent_to_seller: t('returns.statusSentToSeller'),
    };
    return statusMap[statusValue] || statusValue;
  };

  const whSaleProductGroups = useMemo(() => {
    if (activeTab !== 'whSale' || !returns?.length) return [];
    const map = new Map();
    returns.forEach((ord) => {
      const key =
        ord.wb_nm_id != null && ord.wb_nm_id !== ''
          ? `nm:${ord.wb_nm_id}`
          : `sku:${(ord.sku_code || '').trim() || `ord:${ord.id}`}`;
      if (!map.has(key)) {
        map.set(key, { key, orders: [] });
      }
      map.get(key).orders.push(ord);
    });
    return Array.from(map.values()).map((g) => {
      let warehouseReturnsCount = 0;
      let settledReturnsCount = 0;
      g.orders.forEach((o) => {
        const lane = whSaleReturnLane(o);
        if (lane === 'settled') settledReturnsCount += 1;
        else warehouseReturnsCount += 1;
      });
      const first = g.orders[0];
      return {
        ...g,
        representative: first,
        displaySku: first.sku_code || '—',
        displayNm: first.wb_nm_id ?? '—',
        skuName: first.sku_name,
        storageTotal: sumWhStorageCharges(g.orders),
        shipmentTotal: sumWhChargesByType(g.orders, 'packaging_shipment'),
        warehouseReturnsCount,
        settledReturnsCount,
        whLiveStocks: resolveWhStocksForOrders(g.orders, whSaleWhStock),
      };
    });
  }, [activeTab, returns, whSaleWhStock]);

  // Fetch data function
  const fetchReturns = useCallback(async (page) => {
    const seq = ++fetchReturnsSeqRef.current;
    setLoading(true);
    setError('');

    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (activeTab === 'whSale') {
        params.grouped = '1';
        if (appliedWhSaleShippedFrom) params.shipped_date_from = appliedWhSaleShippedFrom;
        if (appliedWhSaleShippedTo) params.shipped_date_to = appliedWhSaleShippedTo;
      } else {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (defectFilter !== '') params.is_defective = defectFilter;
        if (statusFilter) params.status = statusFilter;
      }
      if (activeTab === 'all' && otherPvzFilter !== '') params.other_pvz = otherPvzFilter;
      if (selectedStoreId) params.store_id = selectedStoreId;
      
      let response;
      switch (activeTab) {
        case 'all':
          response = await api.getAllReturns(params);
          // Store available statuses from response
          if (response.statuses) {
            setAvailableStatuses(response.statuses);
          }
          break;
        case 'inTransit':
          response = await api.getReturnsInTransit(params);
          break;
        case 'otherPvz':
          response = await api.getReturnsOtherPvz(params);
          break;
        case 'inProcessing':
          response = await api.getOrdersInProcessing(params);
          break;
        case 'pendingDecision':
          response = await api.getOrdersPendingDecision(params);
          break;
        case 'completed':
          response = await api.getCompletedOrders(params);
          break;
        case 'whSale':
          response = await api.getReturnsWhBilling(params);
          break;
        default:
          response = await api.getAllReturns(params);
          if (response.statuses) {
            setAvailableStatuses(response.statuses);
          }
      }

      if (seq !== fetchReturnsSeqRef.current) {
        return;
      }
      if (activeTab === 'whSale') {
        setWhSaleSummary(
          response.summary || {
            shipments_total: 0,
            sold_orders: 0,
            canceled_orders: 0,
            in_transit_orders: 0,
          },
        );
        setWhSaleWhStock(response.wh_stock || null);
      } else {
        setWhSaleSummary(null);
        setWhSaleWhStock(null);
      }
      setReturns(response.data || []);
      setTotalItems(response.total ?? 0);
      setTotalPages(response.total_pages ?? 0);
      setCurrentPage(response.page ?? page);
    } catch (err) {
      if (seq !== fetchReturnsSeqRef.current) {
        return;
      }
      console.error('Failed to fetch returns:', err);
      setError(t('returns.errorLoadingItems'));
    } finally {
      if (seq === fetchReturnsSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    activeTab,
    debouncedSearch,
    dateFrom,
    dateTo,
    defectFilter,
    statusFilter,
    otherPvzFilter,
    selectedStoreId,
    pageSize,
    appliedWhSaleShippedFrom,
    appliedWhSaleShippedTo,
    t,
  ]);

  useEffect(() => {
    if (activeTab === 'whSale') {
      setDefectFilter('');
      setStatusFilter('');
    } else {
      setWhSaleSummary(null);
      setWhSaleWhStock(null);
      setWhSaleShippedFromDraft('');
      setWhSaleShippedToDraft('');
      setAppliedWhSaleShippedFrom('');
      setAppliedWhSaleShippedTo('');
    }
  }, [activeTab]);

  const bulkBarVisible = activeTab === 'inTransit' || activeTab === 'otherPvz' || activeTab === 'pendingDecision';

  const bulkSelectableRows = useMemo(() => {
    if (activeTab === 'inTransit' || activeTab === 'otherPvz') {
      return returns.filter((r) => r.status === 'awaiting_receipt');
    }
    if (activeTab === 'pendingDecision') {
      return returns.filter((r) => r.status === 'awaiting_seller_decision');
    }
    return [];
  }, [returns, activeTab]);

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [
    activeTab,
    currentPage,
    debouncedSearch,
    dateFrom,
    dateTo,
    defectFilter,
    statusFilter,
    otherPvzFilter,
    selectedStoreId,
    pageSize,
    appliedWhSaleShippedFrom,
    appliedWhSaleShippedTo,
  ]);

  const toggleSelectOrderId = useCallback((id) => {
    setSelectedOrderIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  const runBulkUpdate = useCallback(
    async (body) => {
      setBulkWorking(true);
      setError('');
      try {
        const res = await api.bulkUpdateReturns(body);
        const pack = res.data || {};
        const failed = pack.failed ?? 0;
        const succeeded = pack.succeeded ?? 0;
        if (failed > 0) {
          setError(
            t('returns.bulkPartialFailure', {
              failed,
              total: succeeded + failed,
            }),
          );
        }
        setSelectedOrderIds([]);
        fetchReturns(currentPage);
      } catch (err) {
        console.error('Bulk update failed:', err);
        setError(t('returns.errorUpdateFailed'));
      } finally {
        setBulkWorking(false);
      }
    },
    [currentPage, fetchReturns, t],
  );

  const pendingDecisionSelectedIds = useMemo(
    () =>
      selectedOrderIds.filter((id) =>
        returns.some((r) => r.id === id && r.status === 'awaiting_seller_decision'),
      ),
    [selectedOrderIds, returns],
  );

  const allSelectableIds = useMemo(() => bulkSelectableRows.map((r) => r.id), [bulkSelectableRows]);
  const allOnPageSelected =
    allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedOrderIds.includes(id));

  const applyGoodsOnSaleFilters = useCallback(() => {
    setAppliedWhSaleShippedFrom(whSaleShippedFromDraft);
    setAppliedWhSaleShippedTo(whSaleShippedToDraft);
  }, [whSaleShippedFromDraft, whSaleShippedToDraft]);

  // Reset to page 1 and fetch when filters or tab change
  useEffect(() => {
    setCurrentPage(1);
    fetchReturns(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    debouncedSearch,
    dateFrom,
    dateTo,
    defectFilter,
    statusFilter,
    otherPvzFilter,
    selectedStoreId,
    pageSize,
    appliedWhSaleShippedFrom,
    appliedWhSaleShippedTo,
  ]);

  // Fetch when page changes (but not on initial mount - handled by filter effect above)
  const isInitialMount = React.useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Only fetch if page changed by user action (not from filter reset)
    fetchReturns(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Helper function to check if order should be in current view
  const shouldOrderBeInCurrentView = useCallback((order) => {
    switch (activeTab) {
      case 'inTransit':
      case 'otherPvz':
        return order.status === 'awaiting_receipt';
      case 'inProcessing':
        return ['received_awaiting_check', 'awaiting_for_sale', 'transferring_to_sale', 'awaiting_shipment', 'awaiting_disposal'].includes(order.status);
      case 'pendingDecision':
        return order.status === 'awaiting_seller_decision';
      case 'completed':
        return ['sent_to_seller', 'disposed', 'submitted_for_sale'].includes(order.status);
      case 'all':
        return true;
      default:
        return false;
    }
  }, [activeTab]);
  
  // Helper function to add authentication token to attachment URLs
  const addTokenToAttachmentUrls = useCallback((order) => {
    if (!order || !order.attachments) return order;
    
    const token = api.getToken();
    if (!token) return order;
    
    // Deep clone the order to avoid mutating the original
    const processedOrder = JSON.parse(JSON.stringify(order));
    
    // Process attachments (ReturnsTableSerializer format has attachments at root level)
    if (processedOrder.attachments && Array.isArray(processedOrder.attachments)) {
      processedOrder.attachments = processedOrder.attachments.map(attachment => {
        if (attachment.file_url) {
          // Add token if not already present
          if (!attachment.file_url.includes('token=')) {
            const separator = attachment.file_url.includes('?') ? '&' : '?';
            attachment.file_url = `${attachment.file_url}${separator}token=${encodeURIComponent(token)}`;
          }
        }
        return attachment;
      });
    }
    
    return processedOrder;
  }, []);
  
  // WebSocket handlers for real-time updates
  const handleOrderUpdate = useCallback((updatedOrder) => {
    if (activeTab === 'whSale' || activeTab === 'inTransit' || activeTab === 'otherPvz') {
      requestAnimationFrame(() => fetchReturns(currentPage));
      return;
    }
    // Add authentication token to attachment URLs
    const processedOrder = addTokenToAttachmentUrls(updatedOrder);
    
    // Check if the updated order matches current filters/tab
    // If it's in the current list, update it; otherwise, refresh the list if it might affect the current view
    
    setReturns(prevReturns => {
      // Check if this order is in the current list
      const orderIndex = prevReturns.findIndex(order => order.id === processedOrder.id);
      
      if (orderIndex >= 0) {
        // Order is in current list
        const shouldStayInView = shouldOrderBeInCurrentView(processedOrder);
        
        if (!shouldStayInView) {
          // Order status changed and it should no longer be in current view, remove it
          const newReturns = prevReturns.filter(order => order.id !== processedOrder.id);
          // Update totalItems to reflect the removal
          setTotalItems(prev => Math.max(0, prev - 1));
          return newReturns;
        } else {
          // Order is still in view, update it
          const newReturns = [...prevReturns];
          newReturns[orderIndex] = processedOrder;
          return newReturns;
        }
      } else {
        // Order is not in current list, but might need to be added
        // Only refresh if we're on first page and order should be visible
        if (currentPage === 1 && shouldOrderBeInCurrentView(processedOrder)) {
          // Use requestAnimationFrame to avoid state update during render
          requestAnimationFrame(() => {
            fetchReturns(1);
          });
        }
        
        // Return previous returns (no immediate change)
        return prevReturns;
      }
    });
  }, [activeTab, currentPage, fetchReturns, shouldOrderBeInCurrentView, addTokenToAttachmentUrls]);
  
  const handleOrderCreated = useCallback((newOrder) => {
    if (activeTab === 'whSale' || activeTab === 'inTransit' || activeTab === 'otherPvz') {
      if (currentPage === 1) {
        requestAnimationFrame(() => fetchReturns(1));
      }
      return;
    }
    // For new orders, check if they should appear in current view
    // If on first page of a relevant tab, refresh to show new order
    if (currentPage === 1 && shouldOrderBeInCurrentView(newOrder)) {
      // Refresh to show new order
      requestAnimationFrame(() => {
        fetchReturns(1);
      });
    }
  }, [activeTab, currentPage, fetchReturns, shouldOrderBeInCurrentView]);
  
  // WebSocket push отключён: таблица не обновляется сама при изменениях заказа на сервере
  useOrdersWebSocket(handleOrderUpdate, handleOrderCreated, false);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    // Page reset and fetch will be triggered by the pageSize useEffect
  };

  const handleBooleanChange = async (returnItem, field, value) => {
    const updateKey = `${returnItem.id}-${field}`;
    setUpdating(prev => ({ ...prev, [updateKey]: true }));
    
    try {
      const data = {
        item_fields: {
          [field]: value
        }
      };
      
      await api.updateReturn(returnItem.id, data);
      
      // Refresh the list to get updated data (status might change)
      fetchReturns(currentPage);
    } catch (err) {
      console.error('Failed to update field:', err);
      setError(t('returns.errorUpdateFailed'));
    } finally {
      setUpdating(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  const handleStatusChange = async (returnItem, newStatus) => {
    if (!newStatus || newStatus === returnItem.status) return;
    
    const updateKey = `${returnItem.id}-status`;
    setUpdating(prev => ({ ...prev, [updateKey]: true }));
    
    try {
      await api.updateReturn(returnItem.id, { status: newStatus });
      
      // Refresh the list since status change might move item
      fetchReturns(currentPage);
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(t('returns.errorUpdateFailed'));
    } finally {
      setUpdating(prev => ({ ...prev, [updateKey]: false }));
    }
  };

  // Open status change modal
  const openStatusModal = (returnItem) => {
    setBulkDecisionIds(null);
    setStatusModalItem(returnItem);
    setSelectedStatus('');
    setShowAddressWarning(false);
    setShowStatusModal(true);
  };

  const openBulkStatusModal = () => {
    const ids = selectedOrderIds.filter((id) =>
      returns.some((r) => r.id === id && r.status === 'awaiting_seller_decision'),
    );
    if (!ids.length) return;
    setStatusModalItem(null);
    setBulkDecisionIds(ids);
    setSelectedStatus('');
    setShowAddressWarning(false);
    setShowStatusModal(true);
  };

  // Confirm status change from modal
  const confirmStatusChange = async () => {
    if (!selectedStatus) return;

    if (selectedStatus === 'awaiting_shipment' && !hasDeliveryAddress) {
      setShowAddressWarning(true);
      return;
    }

    const statusToApply = selectedStatus;
    setShowStatusModal(false);
    const idsBulk = bulkDecisionIds;
    const singleItem = statusModalItem;
    setBulkDecisionIds(null);
    setStatusModalItem(null);
    setSelectedStatus('');

    if (idsBulk && idsBulk.length) {
      await runBulkUpdate({ order_ids: idsBulk, status: statusToApply });
    } else if (singleItem) {
      await handleStatusChange(singleItem, statusToApply);
    }
  };

  // Cancel status change modal
  const cancelStatusChange = () => {
    setShowStatusModal(false);
    setStatusModalItem(null);
    setBulkDecisionIds(null);
    setSelectedStatus('');
    setShowAddressWarning(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Display Yes/No/- for boolean values
  const BooleanDisplay = ({ value }) => {
    if (value === null || value === undefined) return <span>-</span>;
    return <span className={value ? 'text-success fw-bold' : 'text-secondary'}>{value ? t('returns.yes') : t('returns.no')}</span>;
  };

  // Toggle for In Transit table (fully editable when awaiting_receipt)
  const BooleanToggle = ({ returnItem, field, value, disabled = false }) => {
    const updateKey = `${returnItem.id}-${field}`;
    const isUpdating = updating[updateKey];
    
    // Determine if field is editable based on order status
    const isEditable = !disabled && (
      (activeTab === 'inTransit' || activeTab === 'otherPvz') && returnItem.status === 'awaiting_receipt'
    );
    
    return (
      <ButtonGroup size="sm" className="bool-toggle">
        <Button
          variant={value === true ? 'warning' : 'outline-secondary'}
          onClick={() => isEditable && handleBooleanChange(returnItem, field, true)}
          disabled={!isEditable || isUpdating}
          className="bool-btn"
        >
          {isUpdating ? <Spinner animation="border" size="sm" /> : t('returns.yes')}
        </Button>
        <Button
          variant={value === false ? 'warning' : 'outline-secondary'}
          onClick={() => isEditable && handleBooleanChange(returnItem, field, false)}
          disabled={!isEditable || isUpdating}
          className="bool-btn"
        >
          {t('returns.no')}
        </Button>
      </ButtonGroup>
    );
  };

  // Get status display text (one-to-one with backend STATUS_CHOICES)
  const getStatusDisplay = (status) => {
    const statusMap = {
      awaiting_receipt: t('returns.statusAwaitingReceipt'),
      received_awaiting_check: t('returns.statusReceivedAwaitingCheck'),
      awaiting_seller_decision: t('returns.statusAwaitingSellerDecision'),
      awaiting_for_sale: t('returns.statusAwaitingForSale'),
      transferring_to_sale: t('returns.statusTransferringToSale'),
      submitted_for_sale: t('returns.statusSubmittedForSale'),
      awaiting_shipment: t('returns.statusAwaitingShipment'),
      awaiting_disposal: t('returns.statusAwaitingDisposal'),
      disposed: t('returns.statusDisposed'),
      sent_to_seller: t('returns.statusSentToSeller'),
    };
    return statusMap[status] || status;
  };

  // Status button that opens modal for confirmation
  const StatusSelect = ({ returnItem }) => {
    const updateKey = `${returnItem.id}-status`;
    const isUpdating = updating[updateKey];
    
    // Can only change status if awaiting_seller_decision
    const canChangeStatus = returnItem.status === 'awaiting_seller_decision';
    
    if (!canChangeStatus) {
      return (
        <span className="status-display">
          {getStatusDisplay(returnItem.status)}
        </span>
      );
    }
    
    return (
      <Button
        variant="outline-warning"
        size="sm"
        className="status-change-btn"
        onClick={() => openStatusModal(returnItem)}
        disabled={isUpdating}
      >
        {isUpdating ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <span className="status-btn-text">{t('returns.chooseAction')}</span>
            <i className="bi bi-chevron-right ms-1"></i>
          </>
        )}
      </Button>
    );
  };

  const ExpandedRowContent = ({ returnItem }) => {
    const attachments = (returnItem.attachments || []).filter(Boolean);
    const photoAttachments = attachments.filter(
      (attachment) => attachment.file_url && attachment.attachment_type === 'photo'
    );
    const videoAttachments = attachments.filter(
      (attachment) => attachment.file_url && attachment.attachment_type === 'video'
    );

    const attachmentCommentSig = (returnItem.attachments || [])
      .map((a) => `${a.id}:${a.user_comment ?? ''}`)
      .join('|');
    const [localPhotoComments, setLocalPhotoComments] = useState({});
    const [savingAttachmentId, setSavingAttachmentId] = useState(null);

    useEffect(() => {
      const next = {};
      photoAttachments.forEach((a) => {
        if (a.id) next[a.id] = a.user_comment ?? '';
      });
      setLocalPhotoComments(next);
    }, [returnItem.id, attachmentCommentSig]);

    const savePhotoComment = async (attachmentId) => {
      const text = localPhotoComments[attachmentId] ?? '';
      setSavingAttachmentId(attachmentId);
      try {
        await api.patchReturnAttachmentComment(attachmentId, text);
        setReturns((prev) =>
          prev.map((order) => {
            if (order.id !== returnItem.id) return order;
            return {
              ...order,
              attachments: (order.attachments || []).map((att) =>
                att.id === attachmentId ? { ...att, user_comment: text } : att
              ),
            };
          })
        );
      } catch (e) {
        console.error(e);
      } finally {
        setSavingAttachmentId(null);
      }
    };

    const openLightbox = (index) => {
      setLightboxPhotos(photoAttachments);
      setLightboxIndex(index);
      setLightboxPhoto(photoAttachments[index]);
    };

    return (
      <tr className="expanded-row">
        <td colSpan={activeTab === 'pendingDecision' ? 10 : 9}>
          <div className="expanded-content p-3">
            <Row>
              <Col md={4}>
                <div className="detail-group mb-3">
                  <label>{t('returns.wbProductPhoto')}</label>
                  <div>
                    {renderWbThumb(returnItem, 120)}
                  </div>
                </div>
                {wbProductCatalogUrl(returnItem) ||
                (user?.is_superuser && (returnItem.wh_portal_products_url || '').trim()) ? (
                  <div className="detail-group mb-3">
                    <label>{t('returns.wbCatalogShort')}</label>
                    <div className="d-flex flex-column gap-1 align-items-start">
                      {wbProductCatalogUrl(returnItem) && (
                        <WbCatalogLink
                          href={wbProductCatalogUrl(returnItem)}
                          label={t('returns.wbOpenOnWildberries')}
                          className="small"
                        />
                      )}
                      <SuperuserWhPortalProductLink
                        orderRow={returnItem}
                        user={user}
                        t={t}
                        className="small"
                      />
                    </div>
                  </div>
                ) : null}
                <div className="detail-group">
                  <label>{t('returns.dimensionsWeight')}</label>
                  <span>{formatItemDimsWeightLine(returnItem, t)}</span>
                </div>
                <div className="detail-group mt-3">
                  <label>{t('returns.receiptDate')}</label>
                  <span>{formatDate(returnItem.receipt_date)}</span>
                </div>
                {returnItem.goods_return_reason && (
                  <div className="detail-group mt-3">
                    <label>{t('returns.goodsReturnReason')}</label>
                    <span>{returnItem.goods_return_reason}</span>
                  </div>
                )}
                {returnItem.status === 'sent_to_seller' && returnItem.seller_tracking_number && (
                  <div className="detail-group mt-3">
                    <label>{t('returns.sellerTrackingNumber')}</label>
                    <span className="tracking-number">{returnItem.seller_tracking_number}</span>
                  </div>
                )}
              </Col>
              <Col md={4}>
                <div className="detail-group">
                  <label>{t('returns.attachedFiles')}</label>
                  <div className="attachments-section">
                    <div className="attachment-type">
                      <strong>{t('returns.photo')}</strong>
                      {photoAttachments.length ? (
                        <div className="attachment-gallery d-flex flex-column gap-3">
                          {photoAttachments.map((attachment, index) => (
                            <div key={attachment.id || attachment.file_url} className="border rounded p-2">
                              <div
                                className="attachment-thumbnail mb-2"
                                onClick={() => openLightbox(index)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && openLightbox(index)}
                                style={{ maxWidth: 200 }}
                              >
                                <img
                                  src={attachment.file_url}
                                  alt={attachment.file_name || t('returns.photo')}
                                  className="attachment-photo"
                                />
                              </div>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                size="sm"
                                className="mb-2"
                                placeholder={t('returns.attachmentCommentPlaceholder')}
                                value={localPhotoComments[attachment.id] ?? ''}
                                onChange={(e) =>
                                  setLocalPhotoComments((prev) => ({
                                    ...prev,
                                    [attachment.id]: e.target.value,
                                  }))
                                }
                              />
                              <Button
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                disabled={savingAttachmentId === attachment.id}
                                onClick={() => savePhotoComment(attachment.id)}
                              >
                                {savingAttachmentId === attachment.id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  t('returns.saveAttachmentComment')
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="no-attachments">-</span>
                      )}
                    </div>
                    <div className="attachment-type">
                      <strong>{t('returns.video')}</strong>
                      {videoAttachments.length ? (
                        <div className="attachment-video-list">
                          {videoAttachments.map((attachment) => (
                            <a
                              key={attachment.id || attachment.file_url}
                              href={attachment.file_url}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="attachment-video-link"
                            >
                              <i className="bi bi-download me-1"></i>
                              {attachment.file_name || t('returns.downloadAttachment')}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="no-attachments">-</span>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col md={8}>
                <div className="detail-group">
                  <label>{t('returns.inspectionComments')}</label>
                  <div className="inspection-box p-2 border rounded">
                    {returnItem.inspection_comments || '-'}
                  </div>
                </div>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                {returnItem.inspection_comments && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="accept-suggestion-btn"
                  >
                    {t('returns.acceptSuggestion')}
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        </td>
      </tr>
    );
  };

  // Mobile card component for both tabs
  const MobileReturnCard = ({ item, isWarehouse = false, showWhBilling = false }) => {
    const isExpanded = expandedRow === item.id;
    const canBulkSelectMobile =
      bulkBarVisible && bulkSelectableRows.some((r) => r.id === item.id);

    return (
      <div 
        className={`mobile-return-card ${isExpanded ? 'expanded' : ''}`}
        onClick={() => setExpandedRow(isExpanded ? null : item.id)}
      >
        {/* Main visible content - always shown */}
        <div className="mobile-card-header">
          {bulkBarVisible && (
            <div className="flex-shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
              <Form.Check
                type="checkbox"
                disabled={bulkWorking || !canBulkSelectMobile}
                checked={selectedOrderIds.includes(item.id)}
                onChange={() => toggleSelectOrderId(item.id)}
                aria-label={t('returns.bulkSelectColumn')}
              />
            </div>
          )}
          <div className="d-flex align-items-start gap-2 flex-grow-1 mobile-card-main">
            <div className="flex-shrink-0 pt-1">{renderWbThumb(item)}</div>
            <div className="flex-grow-1 min-w-0">
              <div className="mobile-product-name">{item.sku_name || '-'}</div>
              <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
              <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
              <div className="mobile-card-date">{formatDate(item.return_request_date)}</div>
            </div>
          </div>
          <div className="mobile-card-actions" onClick={(e) => e.stopPropagation()}>
            {!showWhBilling && <StatusSelect returnItem={item} />}
          </div>
          <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} mobile-expand-icon`}></i>
        </div>

        {/* Expanded content - shown on click */}
        {isExpanded && (
          <div className="mobile-card-details" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">{t('returns.sku')}</span>
              <span className="mobile-detail-value">{item.sku_code || '-'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">{t('returns.barcode')}</span>
              <span className="mobile-detail-value">{item.sku_barcode || '-'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">{t('returns.returnNumber')}</span>
              <span className="mobile-detail-value">{item.return_number || '-'}</span>
            </div>
            <div className="mobile-detail-row">
              <span className="mobile-detail-label">{t('returns.defectiveItem')}</span>
              <span className="mobile-detail-value"><BooleanDisplay value={item.is_defective} /></span>
            </div>
            {isWarehouse ? (
              <>
                <div className="mobile-detail-row">
                  <span className="mobile-detail-label">{t('returns.isDefectAfterCheck')}</span>
                  <span className="mobile-detail-value">
                    <BooleanDisplay value={item.is_defect_after_check} />
                  </span>
                </div>
                <div className="mobile-detail-row">
                  <span className="mobile-detail-label">{t('returns.receiptDate')}</span>
                  <span className="mobile-detail-value">{formatDate(item.receipt_date)}</span>
                </div>
                {item.status === 'sent_to_seller' && item.seller_tracking_number && (
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">{t('returns.sellerTrackingNumber')}</span>
                    <span className="mobile-detail-value tracking-number">{item.seller_tracking_number}</span>
                  </div>
                )}
                {item.inspection_comments && (
                  <div className="mobile-detail-row mobile-detail-full">
                    <span className="mobile-detail-label">{t('returns.inspectionComments')}</span>
                    <div className="mobile-inspection-box">{item.inspection_comments}</div>
                  </div>
                )}
              </>
            ) : null}
            {showWhBilling && (
              <div className="mobile-detail-row mobile-detail-full mt-2">
                <span className="mobile-detail-label">{t('returns.goodsOnSaleMpStateCol')}</span>
                <div className="small mb-2">
                  <span className={goodsOnSaleMpStatusPillClassName(item.wh_mp_fulfillment?.status)}>
                    {goodsOnSaleMpStatusLabel(item.wh_mp_fulfillment, t, language)}
                  </span>
                </div>
                {(item.wh_billing_expenses || []).length > 0 && (
                  <>
                    <span className="mobile-detail-label">{t('returns.whBillingCharges')}</span>
                    <ul className="list-unstyled small mb-0 ps-0">
                      {(item.wh_billing_expenses || []).map((e) => (
                        <li key={e.id} className="mb-1">
                          <span className="text-muted">{formatDate(e.billing_date)}</span>
                          {' · '}
                          {getWhBillingTypeLabel(e.type)}
                          {': '}
                          {Number(e.amount).toLocaleString()} {t('common.currency.yen')}
                          {e.wh_stock_units != null && e.wh_stock_units !== '' && (
                            <span className="text-muted">
                              {' '}({t('billing.whStockUnits')}: {e.wh_stock_units})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Mobile list rendering
  const renderMobileList = (isWarehouse = false, showWhBilling = false) => (
    <div className="mobile-returns-list">
      {returns.map((item) => (
        <MobileReturnCard key={item.id} item={item} isWarehouse={isWarehouse} showWhBilling={showWhBilling} />
      ))}
    </div>
  );

  const renderOtherPvzExpanded = (item) => (
    <tr className="expanded-row">
      <td colSpan={9}>
        <div className="expanded-content p-3">
          <Row>
            <Col md={4}>
              <div className="detail-group">
                <label>{t('returns.destination')}</label>
                <span>{item.dst_office_address || '-'}</span>
              </div>
            </Col>
            <Col md={4}>
              <div className="detail-group">
                <label>{t('returns.readyToReturnDt')}</label>
                <span>{formatDate(item.ready_to_return_dt)}</span>
              </div>
            </Col>
            <Col md={4}>
              <div className="detail-group">
                <label>{t('returns.archiveState')}</label>
                <span>{item.is_status_active_display || '-'}</span>
              </div>
            </Col>
            <Col md={12} className="mt-2">
              <div className="detail-group">
                <label>{t('returns.dimensionsWeight')}</label>
                <span>{formatItemDimsWeightLine(item, t)}</span>
              </div>
            </Col>
          </Row>
        </div>
      </td>
    </tr>
  );

  // In Transit table (otherPvz uses expandable details to keep table compact)
  const renderInTransitTable = () => (
    <Table className="returns-table">
      <thead>
        <tr>
          {bulkBarVisible && (
            <th className="text-center align-middle" style={{ width: 40 }}>
              <Form.Check
                type="checkbox"
                disabled={bulkWorking || !allSelectableIds.length}
                checked={allOnPageSelected}
                onChange={() =>
                  allOnPageSelected ? setSelectedOrderIds([]) : setSelectedOrderIds([...allSelectableIds])
                }
                aria-label={t('returns.bulkSelectPage')}
                title={t('returns.bulkSelectPage')}
              />
            </th>
          )}
          <th>{t('returns.returnRequestDate')}</th>
          <th className="d-none d-sm-table-cell">{t('returns.wbProductPhoto')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.barcode')}</th>
          <th>{t('returns.returnNumber')}</th>
          <th>{t('returns.defectiveItem')}</th>
          <th>{t('returns.decisionOnProduct')}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((item) => (
          <React.Fragment key={item.id}>
            <tr
              className={activeTab === 'otherPvz' ? `expandable-row ${expandedRow === item.id ? 'expanded' : ''}` : ''}
              onClick={activeTab === 'otherPvz' ? () => setExpandedRow(expandedRow === item.id ? null : item.id) : undefined}
            >
              {bulkBarVisible && (
                <td
                  className="text-center align-middle"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    disabled={
                      bulkWorking || !bulkSelectableRows.some((r) => r.id === item.id)
                    }
                    checked={selectedOrderIds.includes(item.id)}
                    onChange={() => toggleSelectOrderId(item.id)}
                    aria-label={t('returns.bulkSelectColumn')}
                  />
                </td>
              )}
              <td>{formatDate(item.return_request_date)}</td>
              <td className="d-none d-sm-table-cell align-middle" onClick={(e) => activeTab === 'otherPvz' && e.stopPropagation()}>
                {renderWbThumb(item)}
              </td>
              <td>
                {activeTab === 'otherPvz' ? (
                  <div className="product-cell">
                    <i className={`bi bi-chevron-${expandedRow === item.id ? 'up' : 'down'} product-expand-icon`}></i>
                    <div>
                      <span className="product-name">{item.sku_name || '-'}</span>
                      <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
                      <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <span className="product-name">{item.sku_name || '-'}</span>
                    <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
                    <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
                  </div>
                )}
              </td>
              <td>{item.sku_code || '-'}</td>
              <td>{item.sku_barcode || '-'}</td>
              <td>{item.return_number || '-'}</td>
              <td>
                <BooleanDisplay value={item.is_defective} />
              </td>
              <td onClick={(e) => activeTab === 'otherPvz' && e.stopPropagation()}>
                <StatusSelect returnItem={item} />
              </td>
            </tr>
            {activeTab === 'otherPvz' && expandedRow === item.id && renderOtherPvzExpanded(item)}
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );

  // Orders in Processing table
  const renderProcessingTable = () => (
    <Table className="returns-table">
      <thead>
        <tr>
          <th>{t('returns.returnRequestDate')}</th>
          <th className="d-none d-sm-table-cell">{t('returns.wbProductPhoto')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.barcode')}</th>
          <th>{t('returns.returnNumber')}</th>
          <th>{t('returns.defectiveItem')}</th>
          <th>{t('returns.status')}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((item) => (
          <tr key={item.id}>
            <td>{formatDate(item.return_request_date)}</td>
            <td className="d-none d-sm-table-cell align-middle">{renderWbThumb(item)}</td>
            <td>
              <span className="product-name">{item.sku_name || '-'}</span>
              <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
              <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
            </td>
              <td>{item.sku_code || '-'}</td>
              <td>{item.sku_barcode || '-'}</td>
              <td>{item.return_number || '-'}</td>
              <td>
                <BooleanDisplay value={item.is_defective} />
              </td>
              <td>
                <span className="status-badge">{getStatusDisplay(item.status)}</span>
              </td>
            </tr>
        ))}
      </tbody>
    </Table>
  );

  // Orders Pending Decision table
  const renderPendingDecisionTable = () => (
    <Table className="returns-table">
      <thead>
        <tr>
          {bulkBarVisible && (
            <th className="text-center align-middle" style={{ width: 40 }}>
              <Form.Check
                type="checkbox"
                disabled={bulkWorking || !allSelectableIds.length}
                checked={allOnPageSelected}
                onChange={() =>
                  allOnPageSelected ? setSelectedOrderIds([]) : setSelectedOrderIds([...allSelectableIds])
                }
                aria-label={t('returns.bulkSelectPage')}
                title={t('returns.bulkSelectPage')}
              />
            </th>
          )}
          <th>{t('returns.returnRequestDate')}</th>
          <th className="d-none d-sm-table-cell">{t('returns.wbProductPhoto')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.barcode')}</th>
          <th>{t('returns.returnNumber')}</th>
          <th>{t('returns.defectiveItem')}</th>
          <th>{t('returns.isDefectAfterCheck')}</th>
          <th>{t('returns.decisionOnProduct')}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((item) => (
          <React.Fragment key={item.id}>
            <tr 
              className={`expandable-row ${expandedRow === item.id ? 'expanded' : ''}`}
              onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
            >
              {bulkBarVisible && (
                <td className="text-center align-middle" onClick={(e) => e.stopPropagation()}>
                  <Form.Check
                    type="checkbox"
                    disabled={
                      bulkWorking || !bulkSelectableRows.some((r) => r.id === item.id)
                    }
                    checked={selectedOrderIds.includes(item.id)}
                    onChange={() => toggleSelectOrderId(item.id)}
                    aria-label={t('returns.bulkSelectColumn')}
                  />
                </td>
              )}
              <td>{formatDate(item.return_request_date)}</td>
              <td className="d-none d-sm-table-cell align-middle" onClick={(e) => e.stopPropagation()}>
                {renderWbThumb(item)}
              </td>
              <td>
                <div className="product-cell">
                  <i className={`bi bi-chevron-${expandedRow === item.id ? 'up' : 'down'} product-expand-icon`}></i>
                  <div>
                    <span className="product-name">{item.sku_name || '-'}</span>
                    <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
                    <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
                  </div>
                </div>
              </td>
              <td>{item.sku_code || '-'}</td>
              <td>{item.sku_barcode || '-'}</td>
              <td>{item.return_number || '-'}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <BooleanDisplay value={item.is_defective} />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <BooleanDisplay value={item.is_defect_after_check} />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <StatusSelect returnItem={item} />
              </td>
            </tr>
            {expandedRow === item.id && <ExpandedRowContent returnItem={item} />}
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );

  // Completed Orders table - with tracking number for sent_to_seller
  const renderCompletedTable = () => (
    <Table className="returns-table">
      <thead>
        <tr>
          <th>{t('returns.returnRequestDate')}</th>
          <th className="d-none d-sm-table-cell">{t('returns.wbProductPhoto')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.barcode')}</th>
          <th>{t('returns.returnNumber')}</th>
          <th>{t('returns.defectiveItem')}</th>
          <th>{t('returns.status')}</th>
          <th>{t('returns.trackingNumber')}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((item) => (
          <tr key={item.id}>
            <td>{formatDate(item.return_request_date)}</td>
            <td className="d-none d-sm-table-cell align-middle">{renderWbThumb(item)}</td>
            <td>
              <span className="product-name">{item.sku_name || '-'}</span>
              <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
              <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
            </td>
              <td>{item.sku_code || '-'}</td>
              <td>{item.sku_barcode || '-'}</td>
              <td>{item.return_number || '-'}</td>
              <td>
                <BooleanDisplay value={item.is_defective} />
              </td>
              <td>
                <span className="status-badge">{getStatusDisplay(item.status)}</span>
              </td>
              <td>
                {item.status === 'sent_to_seller' && item.seller_tracking_number ? (
                  <span className="tracking-number">{item.seller_tracking_number}</span>
                ) : item.customer_tracking_number ? (
                  <span className="tracking-number">{item.customer_tracking_number}</span>
                ) : (
                  '-'
                )}
              </td>
            </tr>
        ))}
      </tbody>
    </Table>
  );

  /** Мобильная вкладка «Товары в продаже»: те же группы по товару, что и в десктопной таблице */
  const renderWhSaleMobile = () => (
    <div className="goods-on-sale-mobile-list mobile-returns-list">
      {whSaleProductGroups.map((g) => {
        const rep = g.representative;
        const expanded = whSaleExpandedProductKey === g.key;
        const stockLine = formatWhLiveStockLine(g.whLiveStocks);
        const stockTitle = whLiveStockTitle(g.whLiveStocks, t);
        return (
          <Card key={g.key} className="mb-3 goods-on-sale-mobile-group border">
            <Card.Body
              className="p-3"
              role="button"
              tabIndex={0}
              onClick={() => setWhSaleExpandedProductKey(expanded ? null : g.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setWhSaleExpandedProductKey(expanded ? null : g.key);
                }
              }}
            >
              <div className="d-flex gap-2 align-items-start">
                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  {renderWbThumb(rep, 56)}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="min-w-0">
                      <i
                        className={`bi bi-chevron-${expanded ? 'up' : 'down'} me-1 text-muted`}
                        aria-hidden
                      />
                      <span className="product-name fw-semibold">{g.skuName || rep.sku_name || '—'}</span>
                      <span className="d-inline-flex flex-column align-items-start gap-0">
                        <WbCatalogLink href={wbProductCatalogUrl(rep)} label={t('returns.wbOpenOnWildberries')} />
                        <SuperuserWhPortalProductLink orderRow={rep} user={user} t={t} />
                      </span>
                      <div className="text-muted small mt-1">{formatItemDimsWeightLine(rep, t)}</div>
                      <div className="text-muted small goods-on-sale-mobile-meta">
                        <span>nmID: {g.displayNm}</span>
                        <span>{t('returns.sku')}: {g.displaySku}</span>
                        <span>{g.orders.length} {t('returns.goodsOnSaleReturnsWord')}</span>
                      </div>
                    </div>
                  </div>
                  <Row className="g-2 mt-2 small">
                    <Col xs={6}>
                      <div className="text-muted">{t('returns.goodsOnSaleOnStockCol')}</div>
                      <div className="fw-semibold" title={stockTitle}>{stockLine}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted">{t('returns.goodsOnSaleSoldCol')}</div>
                      <div className="fw-semibold">{g.settledReturnsCount}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted">{t('returns.goodsOnSaleStorageFeeCol')}</div>
                      <div className="fw-semibold">
                        {g.storageTotal.toLocaleString()} {t('common.currency.yen')}
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted">{t('returns.goodsOnSaleShipmentFeeCol')}</div>
                      <div className="fw-semibold">
                        {g.shipmentTotal.toLocaleString()} {t('common.currency.yen')}
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Card.Body>
            {expanded && (
              <ListGroup variant="flush" className="goods-on-sale-mobile-returns border-top">
                <ListGroup.Item className="px-3 py-2 bg-transparent border-0">
                  <div className="fw-semibold small mb-2">{t('returns.goodsOnSaleReturnsSubtable')}</div>
                </ListGroup.Item>
                {g.orders.map((ord) => {
                  const lane = whSaleReturnLane(ord);
                  const charges = ord.wh_billing_expenses || [];
                  const bySt = whStorageAmountsByType(ord);
                  const sh = charges
                    .filter((e) => e.type === 'packaging_shipment')
                    .reduce((acc, e) => acc + Math.abs(Number(e.amount || 0)), 0);
                  const storageLines = WH_STORAGE_EXPENSE_TYPES.map((typ) => {
                    const amt = bySt[typ];
                    if (!amt) return null;
                    return (
                      <div key={typ}>
                        {getWhBillingTypeLabel(typ)}: {amt.toLocaleString()} {t('common.currency.yen')}
                      </div>
                    );
                  }).filter(Boolean);
                  const rowTone =
                    lane === 'settled' ? 'goods-on-sale-mobile-return--settled' : 'goods-on-sale-mobile-return--warehouse';
                  return (
                    <ListGroup.Item
                      key={ord.id}
                      className={`px-3 py-2 goods-on-sale-mobile-return ${rowTone}`}
                    >
                      <div className="small mb-1">
                        <span className="text-muted">{t('returns.returnNumber')}: </span>
                        <span className="fw-medium">{ord.return_number || '—'}</span>
                        <span className="text-muted ms-2">{t('returns.returnRequestDate')}: </span>
                        {formatDate(ord.return_request_date)}
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">{t('returns.goodsOnSaleMpStateCol')}: </span>
                        <span className={goodsOnSaleMpStatusPillClassName(ord.wh_mp_fulfillment?.status)}>
                          {goodsOnSaleMpStatusLabel(ord.wh_mp_fulfillment, t, language)}
                        </span>
                      </div>
                      <div className="small">
                        <div className="text-muted">{t('returns.whStorageTotal')}</div>
                        {storageLines.length ? storageLines : '—'}
                      </div>
                      <div className="small mt-1">
                        <span className="text-muted">{t('returns.whShipmentTotal')}: </span>
                        {sh.toLocaleString()} {t('common.currency.yen')}
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </Card>
        );
      })}
    </div>
  );

  const renderWhSaleTable = () => (
    <Table responsive className="returns-table goods-on-sale-table">
      <thead>
        <tr>
          <th className="d-none d-md-table-cell">{t('returns.photo')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.goodsOnSaleOnStockCol')}</th>
          <th>{t('returns.goodsOnSaleSoldCol')}</th>
          <th>{t('returns.goodsOnSaleStorageFeeCol')}</th>
          <th>{t('returns.goodsOnSaleShipmentFeeCol')}</th>
        </tr>
      </thead>
      <tbody>
        {whSaleProductGroups.map((g) => {
          const rep = g.representative;
          const expanded = whSaleExpandedProductKey === g.key;
          const stockLine = formatWhLiveStockLine(g.whLiveStocks);
        const stockTitle = whLiveStockTitle(g.whLiveStocks, t);
          return (
            <React.Fragment key={g.key}>
              <tr
                className={`expandable-row ${expanded ? 'expanded' : ''}`}
                onClick={() => setWhSaleExpandedProductKey(expanded ? null : g.key)}
              >
                <td className="d-none d-md-table-cell align-middle" onClick={(e) => e.stopPropagation()}>
                  {renderWbThumb(rep)}
                </td>
                <td className="text-start">
                  <div className="product-cell">
                    <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} product-expand-icon`} />
                    <div>
                      <span className="product-name">{g.skuName || rep.sku_name || '—'}</span>
                      <span className="d-inline-flex flex-column align-items-start gap-0">
                        <WbCatalogLink href={wbProductCatalogUrl(rep)} label={t('returns.wbOpenOnWildberries')} />
                        <SuperuserWhPortalProductLink orderRow={rep} user={user} t={t} />
                      </span>
                      <div className="text-muted small mt-1">{formatItemDimsWeightLine(rep, t)}</div>
                      <div className="text-muted small mt-1">
                        nmID: {g.displayNm} · {t('returns.sku')}: {g.displaySku} · {g.orders.length} {t('returns.goodsOnSaleReturnsWord')}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <span className="text-break">{g.displaySku}</span>
                </td>
                <td>
                  <span className="fw-semibold" title={stockTitle}>{stockLine}</span>
                </td>
                <td>
                  <span className="fw-semibold">{g.settledReturnsCount}</span>
                </td>
                <td>
                  {g.storageTotal.toLocaleString()} {t('common.currency.yen')}
                </td>
                <td>
                  {g.shipmentTotal.toLocaleString()} {t('common.currency.yen')}
                </td>
              </tr>
              {expanded && (
                <tr className="expanded-row">
                  <td colSpan={7} className="p-0">
                    <div className="expanded-content p-3 bg-light">
                      <div className="fw-semibold small mb-2">{t('returns.goodsOnSaleReturnsSubtable')}</div>
                      <Table size="sm" bordered className="mb-0 bg-white">
                        <thead className="table-light">
                          <tr>
                            <th>{t('returns.returnNumber')}</th>
                            <th>{t('returns.returnRequestDate')}</th>
                            <th>{t('returns.goodsOnSaleMpStateCol')}</th>
                            <th>{t('returns.whStorageTotal')}</th>
                            <th>{t('returns.whShipmentTotal')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.orders.map((ord) => {
                            const lane = whSaleReturnLane(ord);
                            const charges = ord.wh_billing_expenses || [];
                            const bySt = whStorageAmountsByType(ord);
                            const sh = charges
                              .filter((e) => e.type === 'packaging_shipment')
                              .reduce((acc, e) => acc + Math.abs(Number(e.amount || 0)), 0);
                            const storageLines = WH_STORAGE_EXPENSE_TYPES.map((typ) => {
                              const amt = bySt[typ];
                              if (!amt) return null;
                              return (
                                <div key={typ}>
                                  {getWhBillingTypeLabel(typ)}: {amt.toLocaleString()} {t('common.currency.yen')}
                                </div>
                              );
                            }).filter(Boolean);
                            return (
                              <tr
                                key={ord.id}
                                className={lane === 'settled' ? 'table-success' : 'table-warning'}
                              >
                                <td>{ord.return_number || '—'}</td>
                                <td>{formatDate(ord.return_request_date)}</td>
                                <td>
                                  <span className={goodsOnSaleMpStatusPillClassName(ord.wh_mp_fulfillment?.status)}>
                                    {goodsOnSaleMpStatusLabel(ord.wh_mp_fulfillment, t, language)}
                                  </span>
                                </td>
                                <td className="small">
                                  {storageLines.length ? storageLines : '—'}
                                </td>
                                <td>
                                  {sh.toLocaleString()} {t('common.currency.yen')}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );

  // All Returns table - unified view with all statuses
  const renderAllReturnsTable = () => (
    <Table className="returns-table">
      <thead>
        <tr>
          <th>{t('returns.returnRequestDate')}</th>
          <th className="d-none d-sm-table-cell">{t('returns.wbProductPhoto')}</th>
          <th>{t('returns.product')}</th>
          <th>{t('returns.sku')}</th>
          <th>{t('returns.barcode')}</th>
          <th>{t('returns.returnNumber')}</th>
          <th>{t('returns.defectiveItem')}</th>
          <th>{t('returns.status')}</th>
          <th>{t('returns.trackingNumber')}</th>
          <th>{t('returns.decisionOnProduct')}</th>
        </tr>
      </thead>
      <tbody>
        {returns.map((item) => (
          <React.Fragment key={item.id}>
            <tr 
              className={`expandable-row ${expandedRow === item.id ? 'expanded' : ''}`}
              onClick={() => setExpandedRow(expandedRow === item.id ? null : item.id)}
            >
              <td>{formatDate(item.return_request_date)}</td>
              <td className="d-none d-sm-table-cell align-middle" onClick={(e) => e.stopPropagation()}>
                {renderWbThumb(item)}
              </td>
              <td>
                <div className="product-cell">
                  <i className={`bi bi-chevron-${expandedRow === item.id ? 'up' : 'down'} product-expand-icon`}></i>
                  <div>
                    <span className="product-name">{item.sku_name || '-'}</span>
                    <span className="d-inline-flex flex-column align-items-start gap-0">
                <WbCatalogLink href={wbProductCatalogUrl(item)} label={t('returns.wbOpenOnWildberries')} />
                <SuperuserWhPortalProductLink orderRow={item} user={user} t={t} />
              </span>
                    <div className="text-muted small mt-1">{formatItemDimsWeightLine(item, t)}</div>
                  </div>
                </div>
              </td>
              <td>{item.sku_code || '-'}</td>
              <td>{item.sku_barcode || '-'}</td>
              <td>{item.return_number || '-'}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <BooleanDisplay value={item.is_defective} />
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span className={`status-badge status-${item.status}`}>
                  {getStatusLabel(item.status)}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                {item.status === 'sent_to_seller' && item.seller_tracking_number ? (
                  <span className="tracking-number">{item.seller_tracking_number}</span>
                ) : item.customer_tracking_number ? (
                  <span className="tracking-number">{item.customer_tracking_number}</span>
                ) : (
                  '-'
                )}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <StatusSelect returnItem={item} />
              </td>
            </tr>
            {expandedRow === item.id && <ExpandedRowContent returnItem={item} />}
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );

  const clearFilters = () => {
    setSearchInput('');
    setDateFrom('');
    setDateTo('');
    setDefectFilter('');
    setStatusFilter('');
    setOtherPvzFilter('');
    setWhSaleShippedFromDraft('');
    setWhSaleShippedToDraft('');
    setAppliedWhSaleShippedFrom('');
    setAppliedWhSaleShippedTo('');
    setCurrentPage(1);
  };

  // Pagination component
  const PaginationControls = () => {
    const items = [];
    const maxVisiblePages = isMobile ? 3 : 5;
    
    // Only show page navigation if more than 1 page
    if (totalPages > 1) {
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      // First page & ellipsis
      if (startPage > 1) {
        items.push(
          <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
            1
          </Pagination.Item>
        );
        if (startPage > 2) {
          items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }
      }

      // Page numbers
      for (let page = startPage; page <= endPage; page++) {
        items.push(
          <Pagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </Pagination.Item>
        );
      }

      // Last page & ellipsis
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }
        items.push(
          <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </Pagination.Item>
        );
      }
    }

    return (
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 mb-3 gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">
            {t('returns.showingItems', {
              from: totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0,
              to: Math.min(currentPage * pageSize, totalItems),
              total: totalItems,
            })}
          </span>
        </div>
        <div className="d-flex align-items-center gap-3">
          {/* Page size selector */}
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{t('returns.itemsPerPage')}</span>
            <Form.Select
              size="sm"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              style={{ width: 'auto' }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Form.Select>
          </div>
          {/* Page navigation */}
          {totalPages > 1 && (
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              />
              {items}
              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              />
            </Pagination>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card className="returns-table-card border-0 shadow-sm">
        {/* Tab Navigation - Hidden when hideTabNavigation is true */}
        {!hideTabNavigation && (
          <Card.Header className="bg-transparent border-0 pt-3">
            <div className="tab-navigation d-flex gap-2 flex-wrap">
              <Button
                variant={activeTab === 'inTransit' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'inTransit' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('inTransit')}
              >
                {t('returns.inTransit')}
              </Button>
              <Button
                variant={activeTab === 'otherPvz' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'otherPvz' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('otherPvz')}
              >
                {t('returns.filterByOtherPvz')}
              </Button>
              <Button
                variant={activeTab === 'inProcessing' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'inProcessing' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('inProcessing')}
              >
                {t('returns.ordersInProcessing')}
              </Button>
              <Button
                variant={activeTab === 'pendingDecision' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'pendingDecision' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('pendingDecision')}
              >
                {t('returns.ordersPendingDecision')}
              </Button>
              <Button
                variant={activeTab === 'completed' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('completed')}
              >
                {t('returns.completedOrders')}
              </Button>
              <Button
                variant={activeTab === 'whSale' ? 'warning' : 'outline-secondary'}
                className={`tab-btn ${activeTab === 'whSale' ? 'active' : ''}`}
                onClick={() => onTabChange && onTabChange('whSale')}
              >
                {t('nav.goodsOnSale')}
              </Button>
            </div>
          </Card.Header>
        )}

        {/* Filters Section - Always visible */}
        <Card.Body className="pb-0">
          {activeTab === 'otherPvz' && (
            <Alert variant="info" className="mb-3 py-2">
              <i className="bi bi-info-circle me-2"></i>
              {t('returns.otherPvzContactBanner')}
            </Alert>
          )}
          {activeTab === 'whSale' && whSaleSummary && (
            <Alert variant="secondary" className="goods-on-sale-summary-bar mb-3 py-2 small">
              <div className="d-flex flex-wrap gap-3 align-items-baseline">
                <span>
                  <span className="text-muted">{t('returns.goodsOnSaleSummaryShipments')}: </span>
                  <strong>{whSaleSummary.shipments_total ?? 0}</strong>
                </span>
                <span>
                  <span className="text-muted">{t('returns.goodsOnSaleSummarySold')}: </span>
                  <strong>{whSaleSummary.sold_orders ?? 0}</strong>
                </span>
                <span>
                  <span className="text-muted">{t('returns.goodsOnSaleSummaryCanceled')}: </span>
                  <strong>{whSaleSummary.canceled_orders ?? 0}</strong>
                </span>
                <span>
                  <span className="text-muted">{t('returns.goodsOnSaleSummaryInTransit')}: </span>
                  <strong>{whSaleSummary.in_transit_orders ?? 0}</strong>
                </span>
              </div>
              <div className="text-muted mt-1">
                {t('returns.goodsOnSaleWhStockLiveHint')}
                {whSaleWhStock?.fetched_at ? (
                  <span>{` · ${whSaleWhStock.fetched_at}`}</span>
                ) : null}
                {whSaleWhStock?.errors?.length ? (
                  <span className="text-danger ms-1">{t('returns.goodsOnSaleWhStockUnavailable')}</span>
                ) : null}
              </div>
            </Alert>
          )}
          <div 
            className="filters-header mb-2 p-2 rounded"
            style={{ 
              backgroundColor: 'var(--orange-light, #fff3e0)', 
              border: '1px solid var(--orange-primary, #ff9800)'
            }}
          >
            <i className="bi bi-funnel me-2"></i>
            {t('returns.filters')}
          </div>

          <div className="filters-content mb-3">
            <Row className="g-2">
              <Col xs={12} md={activeTab === 'whSale' ? 5 : 3}>
                <InputGroup size="sm" className="search-input-group">
                  <InputGroup.Text className="search-icon-container">
                    <i className="bi bi-search search-icon"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder={t('returns.searchPlaceholder')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="search-input"
                  />
                </InputGroup>
              </Col>
              {activeTab === 'whSale' && (
                <>
                  <Col xs={6} md={2}>
                    <Form.Label className="small text-muted mb-0 d-block">
                      {t('returns.goodsOnSaleShippedDateFrom')}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={whSaleShippedFromDraft}
                      onChange={(e) => setWhSaleShippedFromDraft(e.target.value)}
                    />
                  </Col>
                  <Col xs={6} md={2}>
                    <Form.Label className="small text-muted mb-0 d-block">
                      {t('returns.goodsOnSaleShippedDateTo')}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={whSaleShippedToDraft}
                      onChange={(e) => setWhSaleShippedToDraft(e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md="auto" className="d-flex align-items-end">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="text-nowrap"
                      disabled={loading}
                      onClick={applyGoodsOnSaleFilters}
                    >
                      {t('returns.goodsOnSaleApplyFilters')}
                    </Button>
                  </Col>
                </>
              )}
              {activeTab !== 'whSale' && (
                <>
                  <Col md={2}>
                    <Form.Control
                      type="date"
                      size="sm"
                      placeholder={t('returns.dateFrom')}
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Control
                      type="date"
                      size="sm"
                      placeholder={t('returns.dateTo')}
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Select
                      size="sm"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">{t('returns.allStatuses')}</option>
                      {availableStatuses.length > 0 ? (
                        availableStatuses.map((status) => (
                          <option key={status.value} value={status.value}>
                            {getStatusLabel(status.value)}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="awaiting_seller_decision">{t('returns.statusAwaitingSellerDecision')}</option>
                          <option value="awaiting_receipt">{t('returns.statusAwaitingReceipt')}</option>
                          <option value="received_awaiting_check">{t('returns.statusReceivedAwaitingCheck')}</option>
                          <option value="awaiting_for_sale">{t('returns.statusAwaitingForSale')}</option>
                          <option value="transferring_to_sale">{t('returns.statusTransferringToSale')}</option>
                          <option value="submitted_for_sale">{t('returns.statusSubmittedForSale')}</option>
                          <option value="awaiting_shipment">{t('returns.statusAwaitingShipment')}</option>
                          <option value="awaiting_disposal">{t('returns.statusAwaitingDisposal')}</option>
                          <option value="disposed">{t('returns.statusDisposed')}</option>
                          <option value="sent_to_seller">{t('returns.statusSentToSeller')}</option>
                        </>
                      )}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Select
                      size="sm"
                      value={defectFilter}
                      onChange={(e) => setDefectFilter(e.target.value)}
                    >
                      <option value="">{t('returns.filterByDefect')}</option>
                      <option value="true">{t('returns.yes')}</option>
                      <option value="false">{t('returns.no')}</option>
                    </Form.Select>
                  </Col>
                </>
              )}
              {activeTab === 'all' && (
                <Col md={2}>
                  <Form.Select
                    size="sm"
                    value={otherPvzFilter}
                    onChange={(e) => setOtherPvzFilter(e.target.value)}
                  >
                    <option value="">{t('returns.filterByOtherPvz')}</option>
                    <option value="true">{t('returns.otherPvzTrue')}</option>
                    <option value="false">{t('returns.otherPvzFalse')}</option>
                  </Form.Select>
                </Col>
              )}
              <Col md={1}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100"
                  onClick={clearFilters}
                  title={t('returns.clearFilters')}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </Col>
            </Row>
          </div>

          {bulkBarVisible && !loading && returns.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 rounded border bg-light">
              <span className="small text-muted">
                {t('returns.bulkSelectedCount', { count: selectedOrderIds.length })}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={bulkWorking || !bulkSelectableRows.length}
                onClick={() => setSelectedOrderIds(bulkSelectableRows.map((r) => r.id))}
              >
                {t('returns.bulkSelectPage')}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={bulkWorking || !selectedOrderIds.length}
                onClick={() => setSelectedOrderIds([])}
              >
                {t('returns.bulkClearSelection')}
              </Button>
              {activeTab === 'pendingDecision' && (
                <>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={bulkWorking || !pendingDecisionSelectedIds.length}
                    onClick={openBulkStatusModal}
                  >
                    {t('returns.bulkDecisionForSelected')}
                  </Button>
                </>
              )}
              {bulkWorking && (
                <Spinner animation="border" size="sm" className="ms-1" />
              )}
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" />
              <p className="mt-2">{t('returns.loadingItems')}</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && returns.length === 0 && (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-4"></i>
              <p className="mt-2">{t('returns.noItemsFound')}</p>
            </div>
          )}

          {/* Table / Mobile Cards */}
          {!loading && returns.length > 0 && (
            <>
              {isMobile ? (
                activeTab === 'whSale' ? (
                  renderWhSaleMobile()
                ) : (
                  // Mobile card layout: warehouse-style expand for tabs that need full inspection UX; not for "in processing"
                  renderMobileList(
                    activeTab === 'pendingDecision' || activeTab === 'completed' || activeTab === 'all',
                    false,
                  )
                )
              ) : (
                // Desktop table layout
                <div className="table-container">
                  {activeTab === 'all' && renderAllReturnsTable()}
                  {(activeTab === 'inTransit' || activeTab === 'otherPvz') && renderInTransitTable()}
                  {activeTab === 'inProcessing' && renderProcessingTable()}
                  {activeTab === 'pendingDecision' && renderPendingDecisionTable()}
                  {activeTab === 'completed' && renderCompletedTable()}
                  {activeTab === 'whSale' && renderWhSaleTable()}
                </div>
              )}
              
              {/* Pagination */}
              <PaginationControls />
            </>
          )}
        </Card.Body>
      </Card>

      {/* Status Change Modal */}
      <Modal show={showStatusModal} onHide={cancelStatusChange} centered className="status-change-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <i className="bi bi-box-arrow-right me-2 text-warning"></i>
            {t('returns.statusChangeTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3 text-muted">{t('returns.statusChangeMessage')}</p>
          
          {/* Product info or bulk count */}
          {bulkDecisionIds && bulkDecisionIds.length > 0 ? (
            <div className="status-modal-product-info mb-3 p-2 bg-light rounded">
              <small className="text-muted">{t('returns.bulkStatusModalIntro')}</small>
              <div className="fw-bold">{t('returns.bulkStatusModalCount', { count: bulkDecisionIds.length })}</div>
            </div>
          ) : statusModalItem ? (
            <div className="status-modal-product-info mb-3 p-2 bg-light rounded">
              <small className="text-muted">{t('returns.product')}:</small>
              <div className="fw-bold">{statusModalItem.sku_name || '-'}</div>
              <div className="small text-muted mt-1">{formatItemDimsWeightLine(statusModalItem, t)}</div>
            </div>
          ) : null}
          
          {/* Address warning for send to seller */}
          {showAddressWarning && (
            <Alert variant="danger" className="mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t('returns.deliveryAddressRequired')}
            </Alert>
          )}
          
          {/* Status options as radio buttons */}
          <div className="status-options">
            <div 
              className={`status-option-item p-3 mb-2 rounded border ${selectedStatus === 'awaiting_for_sale' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => { setSelectedStatus('awaiting_for_sale'); setShowAddressWarning(false); }}
            >
              <Form.Check
                type="radio"
                id="status-sell"
                name="statusOption"
                label={t('returns.sellOnWB')}
                value="awaiting_for_sale"
                checked={selectedStatus === 'awaiting_for_sale'}
                onChange={(e) => { setSelectedStatus(e.target.value); setShowAddressWarning(false); }}
                className="mb-0"
              />
            </div>
            <div 
              className={`status-option-item p-3 mb-2 rounded border ${selectedStatus === 'awaiting_shipment' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'} ${!hasDeliveryAddress ? 'opacity-75' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => { setSelectedStatus('awaiting_shipment'); setShowAddressWarning(false); }}
            >
              <Form.Check
                type="radio"
                id="status-return"
                name="statusOption"
                label={
                  <span>
                    {t('returns.sendToSeller')}
                    {!hasDeliveryAddress && (
                      <span className="text-danger ms-2 small">
                        <i className="bi bi-exclamation-circle"></i>
                      </span>
                    )}
                  </span>
                }
                value="awaiting_shipment"
                checked={selectedStatus === 'awaiting_shipment'}
                onChange={(e) => { setSelectedStatus(e.target.value); setShowAddressWarning(false); }}
                className="mb-0"
              />
            </div>
            <div 
              className={`status-option-item p-3 mb-2 rounded border ${selectedStatus === 'awaiting_disposal' ? 'border-warning bg-warning bg-opacity-10' : 'border-secondary'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => { setSelectedStatus('awaiting_disposal'); setShowAddressWarning(false); }}
            >
              <Form.Check
                type="radio"
                id="status-dispose"
                name="statusOption"
                label={t('returns.dispose')}
                value="awaiting_disposal"
                checked={selectedStatus === 'awaiting_disposal'}
                onChange={(e) => { setSelectedStatus(e.target.value); setShowAddressWarning(false); }}
                className="mb-0"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={cancelStatusChange}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="warning" 
            onClick={confirmStatusChange}
            disabled={!selectedStatus}
          >
            {t('returns.confirmStatusChange')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Lightbox Modal — клик по тёмному фону закрывает просмотр */}
      <Modal 
        show={!!lightboxPhoto} 
        onHide={() => setLightboxPhoto(null)} 
        fullscreen
        className="photo-lightbox-modal"
        contentClassName="bg-transparent border-0"
      >
        <Modal.Body
          className="photo-lightbox-backdrop p-0 d-flex align-items-center justify-content-center"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="position-relative"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="link"
              className="lightbox-close-btn"
              onClick={() => setLightboxPhoto(null)}
              aria-label={t('common.close')}
            >
              <i className="bi bi-x-lg"></i>
            </Button>

            {lightboxPhotos.length > 1 && (
              <>
                <Button
                  variant="link"
                  className="lightbox-nav-btn lightbox-prev"
                  onClick={() => {
                    const newIndex = lightboxIndex === 0 ? lightboxPhotos.length - 1 : lightboxIndex - 1;
                    setLightboxIndex(newIndex);
                    setLightboxPhoto(lightboxPhotos[newIndex]);
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <Button
                  variant="link"
                  className="lightbox-nav-btn lightbox-next"
                  onClick={() => {
                    const newIndex = lightboxIndex === lightboxPhotos.length - 1 ? 0 : lightboxIndex + 1;
                    setLightboxIndex(newIndex);
                    setLightboxPhoto(lightboxPhotos[newIndex]);
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </>
            )}

            {lightboxPhoto && (
              <div className="lightbox-image-container">
                <img
                  src={lightboxPhoto.file_url}
                  alt={lightboxPhoto.file_name || t('returns.photo')}
                  className="lightbox-image"
                />
              </div>
            )}

            {lightboxPhotos.length > 1 && (
              <div className="lightbox-counter">
                {lightboxIndex + 1} / {lightboxPhotos.length}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ReturnsTable;
